import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getCompanyAdminList, getCompanyList } from '../../../app/modules/Management/core/_requests'
import { getVesselList } from '../../../app/modules/Technical/core/_requests'
import type { Vessel } from '../../../app/modules/Management/core/_models'
import { normalizeAccessibleVessels, useAccessibleVessels } from '../access/accessibleVessels'

/* ── Types ── */
export interface CompanyOption {
  id: number
  name: string
  type: 'company_group' | 'company_admin'
}

export interface GlobalFilterState {
  company: CompanyOption | null
  vessel: Vessel | null
  searchQuery: string
}

interface GlobalFilterContextValue {
  state: GlobalFilterState
  companies: CompanyOption[]
  vessels: Vessel[]
  loading: boolean
  setCompany: (company: CompanyOption | null) => void
  setVessel: (vessel: Vessel | null) => void
  setSearchQuery: (query: string) => void
  clearFilters: () => void
}

const GlobalFilterContext = createContext<GlobalFilterContextValue | null>(null)

/* ── Session persistence keys ── */
const STORAGE_KEY = 'global-filter-state'

function loadPersisted(): GlobalFilterState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { company: null, vessel: null, searchQuery: '' }
}

function persistState(state: GlobalFilterState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch { /* ignore */ }
}

/* ── Provider ── */
export const GlobalFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filterState, setFilterState] = useState<GlobalFilterState>(loadPersisted)
  const [companies, setCompanies] = useState<CompanyOption[]>([])
  const [allVessels, setAllVessels] = useState<Vessel[]>([])
  const [fallbackVessels, setFallbackVessels] = useState<Vessel[]>([])
  const [loading, setLoading] = useState(false)
  const { data: accessibleVessels = [] } = useAccessibleVessels()

  // Fetch lists once on mount - independent promises so companies load instantly
  useEffect(() => {
    let cancelled = false

    getCompanyAdminList?.()
      .then((groupsRaw) => {
        if (cancelled || !Array.isArray(groupsRaw)) return
        const groupOpts: CompanyOption[] = groupsRaw.map((g: any) => ({
          id: Number(g.id),
          name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
          type: 'company_group' as const,
        }))
        setCompanies((prev) => {
          const nonGroups = prev.filter((c) => c.type !== 'company_group')
          return [...groupOpts, ...nonGroups]
        })
      })
      .catch(() => {})

    getCompanyList?.()
      .then((adminsRaw) => {
        if (cancelled || !Array.isArray(adminsRaw)) return
        const adminOpts: CompanyOption[] = adminsRaw.map((a: any) => ({
          id: Number(a.id),
          name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
          type: 'company_admin' as const,
        }))
        setCompanies((prev) => {
          const nonAdmins = prev.filter((c) => c.type !== 'company_admin')
          return [...nonAdmins, ...adminOpts]
        })
      })
      .catch(() => {})

    getVesselList?.()
      .then((vesselsRaw) => {
        if (cancelled || !Array.isArray(vesselsRaw)) return
        setFallbackVessels(vesselsRaw as any)
      })
      .catch(() => {})

    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (Array.isArray(accessibleVessels) && accessibleVessels.length > 0) {
      setAllVessels(normalizeAccessibleVessels(accessibleVessels as any))
    } else if (Array.isArray(fallbackVessels) && fallbackVessels.length > 0) {
      setAllVessels(fallbackVessels)
    } else {
      setAllVessels([])
    }
  }, [accessibleVessels, fallbackVessels])

  // Vessels filtered by selected company
  const filteredVessels = useMemo(() => {
    if (!filterState.company) return allVessels
    const selCompId = Number(filterState.company.id)
    const selCompName = (filterState.company.name || '').trim().toLowerCase()

    return allVessels.filter((v: any) => {
      // 1. Check Group match IDs & Names
      const groupIds = [
        v.companyGroupAdmin?.id,
        v.companyGroupId,
        v.companyGroupAdminId,
        v.companyAdmin?.cgaid?.id,
        v.companyAdmin?.companyGroupAdminId,
        v.companyAdmin?.cgaid,
        v.cgaid?.id,
        v.cgaid,
      ].map(n => Number(n)).filter(n => Number.isFinite(n) && n > 0)

      const groupNames = [
        v.companyGroupAdmin?.name,
        v.companyGroupAdmin?.companyGroupAdminName,
        v.companyAdmin?.cgaid?.companyGroupAdminName,
        v.companyAdmin?.cgaid?.name,
      ].map(s => String(s || '').trim().toLowerCase()).filter(Boolean)

      // 2. Check Subcompany / Company Admin match IDs & Names
      const companyIds = [
        v.companyAdmin?.id,
        v.companyId,
        v.companyAdminId,
      ].map(n => Number(n)).filter(n => Number.isFinite(n) && n > 0)

      const companyNames = [
        v.companyAdmin?.name,
        v.companyAdmin?.companyAdminName,
      ].map(s => String(s || '').trim().toLowerCase()).filter(Boolean)

      if (filterState.company!.type === 'company_group') {
        if (groupIds.includes(selCompId)) return true
        if (selCompName && groupNames.some(gn => gn === selCompName || gn.includes(selCompName) || selCompName.includes(gn))) return true
      }

      if (filterState.company!.type === 'company_admin') {
        if (companyIds.includes(selCompId)) return true
        if (selCompName && companyNames.some(cn => cn === selCompName || cn.includes(selCompName) || selCompName.includes(cn))) return true
      }

      // Fallback: match by ID or Name regardless of type in case type tag differs
      if (groupIds.includes(selCompId) || companyIds.includes(selCompId)) return true
      if (selCompName && (
        groupNames.some(gn => gn === selCompName) ||
        companyNames.some(cn => cn === selCompName)
      )) return true

      return false
    })
  }, [allVessels, filterState.company])

  const setCompany = useCallback((company: CompanyOption | null) => {
    setFilterState(prev => {
      const next = { ...prev, company, vessel: null } // reset vessel when company changes
      persistState(next)
      return next
    })
  }, [])

  const setVessel = useCallback((vessel: Vessel | null) => {
    setFilterState(prev => {
      const next = { ...prev, vessel }
      persistState(next)
      return next
    })
  }, [])

  const setSearchQuery = useCallback((searchQuery: string) => {
    setFilterState(prev => {
      const next = { ...prev, searchQuery }
      persistState(next)
      return next
    })
  }, [])

  const clearFilters = useCallback(() => {
    const next: GlobalFilterState = { company: null, vessel: null, searchQuery: '' }
    setFilterState(next)
    persistState(next)
  }, [])

  const value = useMemo<GlobalFilterContextValue>(() => ({
    state: filterState,
    companies,
    vessels: filteredVessels,
    loading,
    setCompany,
    setVessel,
    setSearchQuery,
    clearFilters,
  }), [filterState, companies, filteredVessels, loading, setCompany, setVessel, setSearchQuery, clearFilters])

  return (
    <GlobalFilterContext.Provider value={value}>
      {children}
    </GlobalFilterContext.Provider>
  )
}

/* ── Hook ── */
export function useGlobalFilter(): GlobalFilterContextValue {
  const ctx = useContext(GlobalFilterContext)
  if (!ctx) throw new Error('useGlobalFilter must be used within GlobalFilterProvider')
  return ctx
}

export default GlobalFilterContext
